/**
 * Менеджер миграций для CSElement
 * Поддерживает версионирование схем и автоматическое применение миграций
 */

import { EventEmitter } from 'events';
import { TypedElementManager } from '../typed-elements/TypedElementManager';
import {
  IMigration,
  IMigrationManager,
  IMigrationStorage,
  IVersionUtils,
  MigrationContext,
  MigrationDirection,
  MigrationPlan,
  MigrationRecord,
  MigrationResult,
  SchemaMigrationConfig,
  DataMigrationConfig
} from '../types/migration-interfaces';

/**
 * Реализация менеджера миграций
 */
export class MigrationManager extends EventEmitter implements IMigrationManager {
  private migrations: Map<string, IMigration> = new Map();
  private storage: IMigrationStorage;
  private versionUtils: IVersionUtils;
  private typedElementManager?: TypedElementManager;

  constructor(storage: IMigrationStorage, versionUtils: IVersionUtils) {
    super();
    this.storage = storage;
    this.versionUtils = versionUtils;
  }

  /**
   * Установка менеджера типизированных элементов
   */
  setTypedElementManager(manager: TypedElementManager): void {
    this.typedElementManager = manager;
  }

  /**
   * Регистрация миграции
   */
  registerMigration(migration: IMigration): void {
    // Проверка валидности версии
    if (!this.versionUtils.isValid(migration.version)) {
      throw new Error(`Invalid version format: ${migration.version}`);
    }

    // Проверка уникальности ID
    if (this.migrations.has(migration.id)) {
      throw new Error(`Migration with ID ${migration.id} already exists`);
    }

    this.migrations.set(migration.id, migration);
  }

  /**
   * Получение всех миграций
   */
  getMigrations(): IMigration[] {
    return Array.from(this.migrations.values());
  }

  /**
   * Получение миграции по ID
   */
  getMigration(id: string): IMigration | undefined {
    return this.migrations.get(id);
  }

  /**
   * Получение истории миграций
   */
  async getHistory(): Promise<MigrationRecord[]> {
    return this.storage.getAllRecords();
  }

  /**
   * Получение текущей версии
   */
  async getCurrentVersion(): Promise<string> {
    const records = await this.storage.getAllRecords();
    const completedRecords = records.filter(r => r.status === 'completed');
    
    if (completedRecords.length === 0) {
      return '0.0.0';
    }

    // Находим максимальную версию
    let maxVersion = '0.0.0';
    for (const record of completedRecords) {
      if (this.versionUtils.compare(record.version, maxVersion) > 0) {
        maxVersion = record.version;
      }
    }

    return maxVersion;
  }

  /**
   * Создание плана миграции
   */
  async createPlan(targetVersion: string): Promise<MigrationPlan> {
    const currentVersion = await this.getCurrentVersion();
    const direction: MigrationDirection = this.versionUtils.compare(targetVersion, currentVersion) > 0 ? 'up' : 'down';
    
    const completedRecords = await this.storage.getRecordsByStatus('completed');
    const completedMigrations = new Set(completedRecords.map(r => r.migrationId));
    
    let candidateMigrations: IMigration[];
    
    if (direction === 'up') {
      // Для upgrade: берем миграции с версией > current и <= target
      candidateMigrations = this.getMigrations().filter(m => {
        const versionCompare = this.versionUtils.compare(m.version, currentVersion);
        const targetCompare = this.versionUtils.compare(m.version, targetVersion);
        return versionCompare > 0 && targetCompare <= 0 && !completedMigrations.has(m.id);
      });
    } else {
      // Для downgrade: берем выполненные миграции с версией > target и <= current
      candidateMigrations = this.getMigrations().filter(m => {
        const versionCompare = this.versionUtils.compare(m.version, targetVersion);
        const currentCompare = this.versionUtils.compare(m.version, currentVersion);
        return versionCompare > 0 && currentCompare <= 0 && completedMigrations.has(m.id);
      });
    }

    // Сортировка по версии
    candidateMigrations.sort((a, b) => {
      const compare = this.versionUtils.compare(a.version, b.version);
      return direction === 'up' ? compare : -compare;
    });

    // Проверка зависимостей и создание порядка выполнения
    const executionOrder = this.resolveDependencies(candidateMigrations, direction);
    
    const warnings: string[] = [];
    
    // Проверка возможности применения каждой миграции
    for (const migration of candidateMigrations) {
      const context: MigrationContext = {
        fromVersion: currentVersion,
        toVersion: targetVersion,
        direction,
        schemaName: undefined
      };

      const canApply = direction === 'up' 
        ? await migration.canApply(context)
        : await migration.canRollback(context);

      if (!canApply) {
        warnings.push(`Migration ${migration.id} cannot be ${direction === 'up' ? 'applied' : 'rolled back'}`);
      }
    }

    return {
      migrations: candidateMigrations,
      executionOrder,
      targetVersion,
      currentVersion,
      direction,
      warnings
    };
  }

  /**
   * Применение миграций
   */
  async migrate(targetVersion?: string): Promise<MigrationResult[]> {
    if (!targetVersion) {
      // Если версия не указана, применяем все доступные миграции
      const migrations = this.getMigrations();
      if (migrations.length === 0) {
        return [];
      }
      
      // Находим максимальную версию
      targetVersion = migrations.reduce((max, m) => 
        this.versionUtils.compare(m.version, max) > 0 ? m.version : max, '0.0.0');
    }

    const plan = await this.createPlan(targetVersion);
    
    this.emit('plan:start', { plan });
    
    const results: MigrationResult[] = [];
    
    for (const migrationId of plan.executionOrder) {
      const migration = this.migrations.get(migrationId);
      if (!migration) {
        continue;
      }

      const context: MigrationContext = {
        fromVersion: plan.currentVersion,
        toVersion: plan.targetVersion,
        direction: plan.direction,
        transactionId: `migration_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
      };

      const result = await this.executeMigration(migration, context);
      results.push(result);
      
      if (!result.success) {
        this.emit('migration:error', { migration, error: new Error(result.error || 'Unknown error') });
        break;
      }
    }
    
    this.emit('plan:complete', { plan, results });
    
    return results;
  }

  /**
   * Откат миграций
   */
  async rollback(targetVersion?: string): Promise<MigrationResult[]> {
    if (!targetVersion) {
      // Если версия не указана, откатываем на одну версию назад
      const records = await this.storage.getRecordsByStatus('completed');
      const versions = records.map(r => r.version).sort((a, b) => this.versionUtils.compare(b, a));
      targetVersion = versions[1] || '0.0.0';
    }

    return this.migrate(targetVersion);
  }

  /**
   * Проверка состояния миграций
   */
  async checkStatus() {
    const currentVersion = await this.getCurrentVersion();
    const allMigrations = this.getMigrations();
    const completedRecords = await this.storage.getRecordsByStatus('completed');
    const completedIds = new Set(completedRecords.map(r => r.migrationId));
    
    const pendingMigrations = allMigrations
      .filter(m => !completedIds.has(m.id))
      .map(m => m.id);
    
    const lastMigration = completedRecords
      .sort((a, b) => b.completedAt!.getTime() - a.completedAt!.getTime())[0];

    return {
      currentVersion,
      pendingMigrations,
      lastMigration
    };
  }

    /**
   * Создание миграции схемы
   */
  createSchemaMigration(config: SchemaMigrationConfig): IMigration {
    const self = this;
    
    const migration: IMigration = {
      id: `schema_${config.schemaName}_${Date.now()}`,
      version: config.newSchema.version || '1.0.0',
      description: `Schema migration for ${config.schemaName}`,
      type: 'schema',
      dependencies: [],

      async up(_context: MigrationContext): Promise<MigrationResult> {
        const startTime = Date.now();
        
        try {
          if (!self.typedElementManager) {
            throw new Error('TypedElementManager not available');
          }

          // Обновляем схему
          self.typedElementManager.registerSchema(config.newSchema);
          
          // Мигрируем существующие элементы
          const elements = self.typedElementManager.findElementsByType(config.schemaName);
          const processedCount = elements.length;

          return {
            success: true,
            processedCount,
            executionTime: Date.now() - startTime
          };
        } catch (error) {
          return {
            success: false,
            error: error instanceof Error ? error.message : 'Unknown error',
            executionTime: Date.now() - startTime
          };
        }
      },

      async down(_context: MigrationContext): Promise<MigrationResult> {
        const startTime = Date.now();
        
        try {
          if (!self.typedElementManager) {
            throw new Error('TypedElementManager not available');
          }

          // Восстанавливаем старую схему
          self.typedElementManager.registerSchema(config.oldSchema);
          
          // Мигрируем элементы обратно
          const elements = self.typedElementManager.findElementsByType(config.schemaName);
          const processedCount = elements.length;

          return {
            success: true,
            processedCount,
            executionTime: Date.now() - startTime
          };
        } catch (error) {
          return {
            success: false,
            error: error instanceof Error ? error.message : 'Unknown error',
            executionTime: Date.now() - startTime
          };
        }
      },

      async canApply(_context: MigrationContext): Promise<boolean> {
        return self.typedElementManager !== undefined;
      },

      async canRollback(_context: MigrationContext): Promise<boolean> {
        return self.typedElementManager !== undefined;
      },

      async validate(_context: MigrationContext): Promise<boolean> {
        if (!self.typedElementManager) {
          return false;
        }
        
        try {
          const elements = self.typedElementManager.findElementsByType(config.schemaName);
          
          for (const element of elements) {
            const validation = await (element as any).validate();
            if (!validation.isValid) {
              return false;
            }
          }
          
          return true;
        } catch {
          return false;
        }
      }
    };

    return migration;
  }

  /**
   * Создание миграции данных
   */
  createDataMigration(config: DataMigrationConfig & { id: string; version: string; description: string }): IMigration {
    const migration: IMigration = {
      id: config.id,
      version: config.version,
      description: config.description,
      type: 'data',
      dependencies: [],

      async up(_context: MigrationContext): Promise<MigrationResult> {
        const startTime = Date.now();
        
        try {
          // Здесь нужно получить все элементы и применить трансформацию
          // Для простоты используем заглушку
          const processedCount = 0;
          
          return {
            success: true,
            processedCount,
            executionTime: Date.now() - startTime
          };
        } catch (error) {
          return {
            success: false,
            error: error instanceof Error ? error.message : 'Unknown error',
            executionTime: Date.now() - startTime
          };
        }
      },

      async down(_context: MigrationContext): Promise<MigrationResult> {
        return {
          success: true,
          processedCount: 0,
          executionTime: 0
        };
      },

      async canApply(_context: MigrationContext): Promise<boolean> {
        return true;
      },

      async canRollback(_context: MigrationContext): Promise<boolean> {
        return true;
      },

      async validate(_context: MigrationContext): Promise<boolean> {
        return true;
      }
    };

    return migration;
  }

  /**
   * Валидация целостности
   */
  async validateIntegrity(): Promise<boolean> {
    try {
      const records = await this.storage.getAllRecords();
      
      for (const record of records) {
        const migration = this.migrations.get(record.migrationId);
        if (!migration) {
          return false;
        }
        
        if (record.status === 'completed') {
          const isValid = await migration.validate(record.context);
          if (!isValid) {
            return false;
          }
        }
      }
      
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Сброс состояния миграций
   */
  async reset(): Promise<void> {
    await this.storage.clear();
  }

  /**
   * Выполнение миграции
   */
  private async executeMigration(migration: IMigration, context: MigrationContext): Promise<MigrationResult> {
    const record: MigrationRecord = {
      migrationId: migration.id,
      version: migration.version,
      status: 'running',
      startedAt: new Date(),
      context
    };

    await this.storage.saveRecord(record);
    
    this.emit('migration:start', { migration, context });
    
    try {
      const result = context.direction === 'up' 
        ? await migration.up(context)
        : await migration.down(context);
      
      record.status = result.success ? 'completed' : 'failed';
      record.completedAt = new Date();
      record.result = result;
      record.executionTime = result.executionTime;
      
      await this.storage.saveRecord(record);
      
      this.emit('migration:complete', { migration, result });
      
      return result;
    } catch (error) {
      record.status = 'failed';
      record.completedAt = new Date();
      record.result = {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
      
      await this.storage.saveRecord(record);
      
      this.emit('migration:error', { migration, error: error as Error });
      
      return record.result;
    }
  }

  /**
   * Разрешение зависимостей миграций
   */
  private resolveDependencies(migrations: IMigration[], direction: MigrationDirection): string[] {
    const visited = new Set<string>();
    const visiting = new Set<string>();
    const result: string[] = [];

    const visit = (migration: IMigration) => {
      if (visiting.has(migration.id)) {
        throw new Error(`Circular dependency detected: ${migration.id}`);
      }
      
      if (visited.has(migration.id)) {
        return;
      }

      visiting.add(migration.id);

      for (const depId of migration.dependencies) {
        const dep = this.migrations.get(depId);
        if (dep && migrations.includes(dep)) {
          visit(dep);
        }
      }

      visiting.delete(migration.id);
      visited.add(migration.id);
      result.push(migration.id);
    };

    for (const migration of migrations) {
      if (!visited.has(migration.id)) {
        visit(migration);
      }
    }

    return direction === 'down' ? result.reverse() : result;
  }


} 